import { Button, Input } from '@/components/ui';
import { useProductSearch } from '@/hooks/pos';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatCurrency } from '../mock-data';
import type { CrossSellProduct } from '../types';
import { useWizardStore } from '../wizard-store';

/**
 * Cross-sell product search component for rental wizard (Story Q-XSL-1).
 * Allows searching and adding products to the rental checkout.
 *
 * Uses the POS product search API (`/api/v1/products?search=...`)
 * and maps results to CrossSellProduct for the wizard store.
 */
export function CrossSellProductSearch() {
  const { t } = useTranslation();
  const {
    crossSellItems,
    addCrossSellItem,
    removeCrossSellItem,
    updateCrossSellQuantity,
    getCrossSellTotal,
  } = useWizardStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Use POS product search hook (TanStack Query)
  const { data: searchResults, isLoading: isSearching } = useProductSearch(
    { search: debouncedTerm, limit: 10 },
    { enabled: debouncedTerm.length >= 2 }
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show dropdown when we have results
  useEffect(() => {
    if (debouncedTerm.length >= 2) {
      setIsDropdownOpen(true);
    }
  }, [debouncedTerm, searchResults]);

  const handleAddProduct = useCallback(
    (product: CrossSellProduct) => {
      addCrossSellItem(product, 1);
      setSearchTerm('');
      setDebouncedTerm('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    },
    [addCrossSellItem]
  );

  const handleQuantityChange = useCallback(
    (itemId: string, value: string) => {
      const qty = parseInt(value, 10);
      if (!isNaN(qty) && qty >= 1 && qty <= 99) {
        updateCrossSellQuantity(itemId, qty);
      }
    },
    [updateCrossSellQuantity]
  );

  // Check if a product is already in the cart
  const isInCart = useCallback(
    (productId: string): boolean => {
      return crossSellItems.some(item => item.product.id === productId);
    },
    [crossSellItems]
  );

  const crossSellTotal = getCrossSellTotal();

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Search input */}
      <div className="relative">
        <div className="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <Input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onFocus={() => {
              if (debouncedTerm.length >= 2) setIsDropdownOpen(true);
            }}
            placeholder={t('rental.crossSell.searchPlaceholder')}
            className="pl-10"
          />
        </div>

        {/* Search results dropdown */}
        {isDropdownOpen && debouncedTerm.length >= 2 && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
            {isSearching && (
              <div className="p-3 text-center text-sm text-muted-foreground">
                <svg
                  className="mx-auto mb-1 h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('rental.crossSell.searching')}
              </div>
            )}

            {!isSearching && searchResults && searchResults.length === 0 && (
              <div className="p-3 text-center text-sm text-muted-foreground">
                {t('rental.crossSell.noResults')}
              </div>
            )}

            {!isSearching && searchResults && searchResults.length > 0 && (
              <ul className="max-h-64 overflow-y-auto py-1">
                {searchResults.map(product => {
                  const alreadyInCart = isInCart(product.id);
                  const crossSellProduct: CrossSellProduct = {
                    id: product.id,
                    sku: product.sku,
                    name: product.name,
                    barcode: product.barcode,
                    category: product.category,
                    price: product.price,
                    vatRate: product.vatRate,
                    stock: product.stock,
                    unit: product.unit,
                  };
                  return (
                    <li
                      key={product.id}
                      className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-accent"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {product.name}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{product.sku}</span>
                          <span className="text-muted-foreground/50">|</span>
                          <span>{formatCurrency(product.price)}</span>
                          <span className="text-muted-foreground/50">|</span>
                          <span>
                            {t('rental.crossSell.stock')}: {product.stock} {product.unit}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant={alreadyInCart ? 'outline' : 'default'}
                        size="sm"
                        disabled={alreadyInCart || product.stock <= 0}
                        onClick={() => handleAddProduct(crossSellProduct)}
                        className="shrink-0 text-xs"
                      >
                        {alreadyInCart ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-1 h-3.5 w-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                            {t('rental.crossSell.add')}
                          </>
                        )}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Selected items list */}
      {crossSellItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-muted-foreground/30 p-4 text-center text-sm text-muted-foreground">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto mb-1 h-5 w-5 text-muted-foreground/50"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
          {t('rental.crossSell.emptyCart')}
        </div>
      ) : (
        <div className="space-y-2">
          {crossSellItems.map(item => (
            <div
              key={item.id}
              className="flex items-center gap-3 rounded-lg border bg-card p-3 text-sm"
            >
              {/* Product info */}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{item.product.name}</div>
                <div className="text-xs text-muted-foreground">
                  {item.product.sku} &middot; {formatCurrency(item.unitPrice)}/{item.product.unit}
                </div>
              </div>

              {/* Quantity selector */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    if (item.quantity > 1) {
                      updateCrossSellQuantity(item.id, item.quantity - 1);
                    }
                  }}
                  disabled={item.quantity <= 1}
                  aria-label={`${t('rental.crossSell.quantity')} -1`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                </Button>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={item.quantity}
                  onChange={e => handleQuantityChange(item.id, e.target.value)}
                  className="h-7 w-12 rounded border bg-background text-center text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  aria-label={t('rental.crossSell.quantity')}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    if (item.quantity < 99) {
                      updateCrossSellQuantity(item.id, item.quantity + 1);
                    }
                  }}
                  disabled={item.quantity >= 99}
                  aria-label={`${t('rental.crossSell.quantity')} +1`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </Button>
              </div>

              {/* Line total */}
              <div className="w-24 text-right font-medium text-foreground">
                {formatCurrency(item.lineTotal)}
              </div>

              {/* Remove button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeCrossSellItem(item.id)}
                aria-label={t('rental.crossSell.remove')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          ))}

          {/* Subtotal */}
          <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm">
            <span className="font-medium text-muted-foreground">
              {t('rental.crossSell.subtotal')} ({crossSellItems.length} {t('rental.crossSell.pcs')})
            </span>
            <span className="font-semibold text-foreground">{formatCurrency(crossSellTotal)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
